import { useEffect, useState } from 'react';
import axios from 'axios';
import Button from '../../ui/Button';
import Icon from '../../ui/Icon';
import Input from '../../ui/Input';
import Sheet from '../../ui/Sheet';
import { useAtualizacaoContexto } from '../../contextos/AtualizacaoContexto';
import useAvisos from '../../hooks/useAviso';
import { mensagemDeErro } from '../../utils/Utils';
import styles from './ApagarDadosSheet.module.css';

const API_URL = process.env.REACT_APP_API_URL + '/usuarios/apagar-dados';

type Alvo = 'movimentacoes' | 'contas' | 'cartoes' | 'categorias';

const OPCOES: { id: Alvo; rotulo: string; icone: string; descricao: string }[] = [
  {
    id: 'movimentacoes',
    rotulo: 'Movimentações',
    icone: 'currency_exchange',
    descricao: 'Todas as transações, despesas fixas e parcelamentos.'
  },
  {
    id: 'contas',
    rotulo: 'Contas',
    icone: 'assured_workload',
    descricao: 'Movimentações mantidas ficam sem vínculo de conta.'
  },
  {
    id: 'cartoes',
    rotulo: 'Cartões',
    icone: 'credit_card',
    descricao: 'Movimentações mantidas ficam sem vínculo de cartão.'
  },
  {
    id: 'categorias',
    rotulo: 'Categorias e metas',
    icone: 'category',
    descricao: 'As metas fazem parte das categorias e serão apagadas junto.'
  }
];

export type ApagarDadosSheetProps = {
  aberto: boolean;
  onFechar: () => void;
};

/**
 * Zona de perigo: reset da conta, total ou granular. A senha é revalidada no
 * SERVIDOR — o sheet só coleta a escolha e a confirmação consciente.
 */
export default function ApagarDadosSheet({ aberto, onFechar }: ApagarDadosSheetProps) {
  const { setAtualizar } = useAtualizacaoContexto();
  const { adicionarAviso } = useAvisos();

  const [alvos, setAlvos] = useState<Alvo[]>([]);
  const [senha, setSenha] = useState('');
  const [erroSenha, setErroSenha] = useState('');
  const [apagando, setApagando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setAlvos([]);
    setSenha('');
    setErroSenha('');
  }, [aberto]);

  const alternar = (alvo: Alvo) =>
    setAlvos((atuais) =>
      atuais.includes(alvo) ? atuais.filter((a) => a !== alvo) : [...atuais, alvo]
    );

  const tudoSelecionado = alvos.length === OPCOES.length;
  const selecionarTudo = () => setAlvos(tudoSelecionado ? [] : OPCOES.map((o) => o.id));

  const apagar = async () => {
    if (alvos.length === 0 || !senha || apagando) return;
    setErroSenha('');
    setApagando(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        API_URL,
        { senha, alvos },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      adicionarAviso(
        tudoSelecionado ? 'Todos os dados foram apagados.' : 'Os dados selecionados foram apagados.',
        'sucesso'
      );
      setAtualizar((atual) => !atual);
      onFechar();
    } catch (err) {
      setErroSenha(mensagemDeErro(err) ?? 'Não foi possível apagar os dados. Tente novamente.');
    } finally {
      setApagando(false);
    }
  };

  const rodape = (
    <>
      <Button variante="fantasma" onClick={onFechar}>
        Cancelar
      </Button>
      <Button
        variante="perigo"
        icone="delete_forever"
        carregando={apagando}
        disabled={alvos.length === 0 || !senha}
        onClick={apagar}
      >
        Apagar definitivamente
      </Button>
    </>
  );

  return (
    <Sheet
      aberto={aberto}
      onFechar={onFechar}
      titulo="Apagar dados da conta"
      subtitulo="Escolha o que apagar e confirme com a sua senha."
      largura={520}
      rodape={rodape}
    >
      <div className={styles.corpo}>
        <div className={styles.alerta}>
          <Icon nome="warning" tamanho={20} />
          <span>
            Esta ação é <strong>irreversível</strong>: os dados apagados não podem ser recuperados e
            deixam de aparecer em todos os relatórios.
          </span>
        </div>

        <div className={styles.selecaoTopo}>
          <span className={styles.selecaoTitulo}>O que apagar</span>
          <button type="button" className={styles.selecionarTudo} onClick={selecionarTudo}>
            {tudoSelecionado ? 'Desmarcar tudo' : 'Selecionar tudo'}
          </button>
        </div>

        <div className={styles.opcoes}>
          {OPCOES.map((opcao) => {
            const ativa = alvos.includes(opcao.id);
            return (
              <button
                key={opcao.id}
                type="button"
                className={`${styles.opcao} ${ativa ? styles.opcaoAtiva : ''}`}
                onClick={() => alternar(opcao.id)}
              >
                <span className={`${styles.caixa} ${ativa ? styles.caixaMarcada : ''}`}>
                  {ativa && <Icon nome="check" tamanho={14} />}
                </span>
                <span className={styles.opcaoIcone}>
                  <Icon nome={opcao.icone} tamanho={20} />
                </span>
                <span className={styles.opcaoTextos}>
                  <span className={styles.opcaoRotulo}>{opcao.rotulo}</span>
                  <span className={styles.opcaoDescricao}>{opcao.descricao}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className={styles.confirmacao}>
          <Input
            label="Confirme com a sua senha"
            tipo="password"
            icone="lock"
            valor={senha}
            onChange={(valor) => {
              setSenha(valor);
              setErroSenha('');
            }}
            placeholder="Sua senha do Finant"
            erro={erroSenha || undefined}
            onEnter={apagar}
          />
        </div>
      </div>
    </Sheet>
  );
}
